/**
 * Project Slice
 * Manages project state and operations
 * 
 * NOTE: Fixed bug from original - line 30 was comparing project.id === project.id (always true)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';

// Persistence keys
const STORAGE_KEY_CURRENT_PROJECT = 'assetrac_current_project';

// Helper to load currentProject from localStorage
const loadCurrentProjectFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CURRENT_PROJECT);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Helper to save currentProject to localStorage
const saveCurrentProjectToStorage = (project) => {
  try {
    if (project) {
      localStorage.setItem(STORAGE_KEY_CURRENT_PROJECT, JSON.stringify(project));
    } else {
      localStorage.removeItem(STORAGE_KEY_CURRENT_PROJECT);
    }
  } catch {
    // Silently fail if localStorage is not available
  }
};

const initialState = {
  projectList: [],
  inactiveProjectList: [],
  projectsLoaded: false,
  currentProject: loadCurrentProjectFromStorage(),
  loading: false,
  error: null,
};

// Async thunks
export const fetchAllProjects = createAsyncThunk(
  'project/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.listProjects();
      const active = (result.items || []).filter(p => p.status === 'active');
      const inactive = (result.items || []).filter(p => p.status !== 'active');
      return { active, inactive };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProjectsByCustomer = createAsyncThunk(
  'project/fetchByCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      // ASS-84: fetch once with no status filter -- the backend's `status`
      // query param is optional (backend/index.js's getProjectsByCustomer),
      // and omitting it returns every status for this customer in one
      // query. Split into active-list vs inactive-list client-side.
      // Previously this made two filtered requests (status=active,
      // status=inactive), which meant 'draft' projects were never
      // fetched from the backend at all -- no client-side filter fix
      // alone could have shown them; the network call itself excluded
      // them. 'draft' now lands in the active-equivalent bucket so R-22's
      // Draft chip can render for it; only 'inactive' is excluded from
      // the visible list.
      const result = await api.getProjectsByCustomer(customerId, {});
      const items = result.items || [];
      const active = items.filter(p => p.status !== 'inactive');
      const inactive = items.filter(p => p.status === 'inactive');

      return { active, inactive };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProject = createAsyncThunk(
  'project/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const result = await api.getProject(id);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProjectAction = createAsyncThunk(
  'project/create',
  async (data, { rejectWithValue }) => {
    try {
      const result = await api.createProject(data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectAction = createAsyncThunk(
  'project/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await api.updateProject(id, data);
      return result.item;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectAction = createAsyncThunk(
  'project/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.deleteProject(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    initProjects: () => initialState,
    
    setProjectList: (state, action) => {
      state.projectList = action.payload;
    },
    
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
      saveCurrentProjectToStorage(action.payload);
    },
    
    addProjectList: (state, action) => {
      state.projectList = [...state.projectList, ...action.payload];
      state.projectsLoaded = true;
    },
    
    /**
     * Update a project in the appropriate list
     * FIXED: Original had bug where it compared project.id === project.id (always true)
     */
    updateProject: (state, action) => {
      const { project } = action.payload;
      if (!project) return;
      
      // FIXED: Changed from (project) => project.id === project.id
      // to (p) => p.id === project.id
      const foundActiveProject = state.projectList.find(
        (p) => p.id === project.id
      );
      
      const isActive = project.status === 'active' || project.status === 'activating';
      const isInactive = project.status === 'inactive' || project.status === 'deactivating';
      
      if (foundActiveProject && isActive) {
        // Update in active list
        state.projectList = state.projectList.map(p => 
          p.id === project.id ? project : p
        );
      } else if (!foundActiveProject && isActive) {
        // Move from inactive to active
        state.inactiveProjectList = state.inactiveProjectList.filter(
          p => p.id !== project.id
        );
        state.projectList = [...state.projectList, project];
      } else if (foundActiveProject && isInactive) {
        // Move from active to inactive
        state.projectList = state.projectList.filter(p => p.id !== project.id);
        state.inactiveProjectList = [...state.inactiveProjectList, project];
      } else {
        // Update in inactive list
        state.inactiveProjectList = state.inactiveProjectList.map(p => 
          p.id === project.id ? project : p
        );
      }
    },
    
    addProject: (state, action) => {
      state.projectList = [...state.projectList, action.payload];
    },
    
    addInactiveProjectList: (state, action) => {
      state.inactiveProjectList = [...state.inactiveProjectList, ...action.payload];
    },
    
    setProjectProjectArea: (state, action) => {
      const { projectArea } = action.payload;
      const projectIndex = state.projectList.findIndex(
        p => p.id === projectArea.project_id
      );
      
      if (projectIndex !== -1) {
        const project = state.projectList[projectIndex];
        const updatedProject = {
          ...project,
          projectAreas: {
            ...project.projectAreas,
            items: (project.projectAreas?.items || []).map(pa => 
              pa.id === projectArea.id ? projectArea : pa
            ),
          },
        };
        state.projectList[projectIndex] = updatedProject;
      }
    },
    
    setProject: (state, action) => {
      const { project } = action.payload;
      state.projectList = state.projectList.map(p => 
        p.id === project.id ? project : p
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // AST-125 PS-1: a fresh sign-in starts at "Projects" -- the last project
      // visited is not reopened. currentProject is persisted to localStorage
      // (above), so without this it came back after login. Matched by type
      // string, not imported, to avoid an auth <-> project slice import cycle.
      .addCase('auth/signIn/fulfilled', (state, action) => {
        if (action.payload?.challengeName) return; // still mid-login
        state.currentProject = null;
        saveCurrentProjectToStorage(null);
      })
      // Fetch all projects
      .addCase(fetchAllProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projectList = action.payload.active;
        state.inactiveProjectList = action.payload.inactive;
        state.projectsLoaded = true;
      })
      .addCase(fetchAllProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch by customer
      .addCase(fetchProjectsByCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectsByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.projectList = action.payload.active;
        state.inactiveProjectList = action.payload.inactive;
        state.projectsLoaded = true;
      })
      .addCase(fetchProjectsByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch one
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = action.payload;
        saveCurrentProjectToStorage(action.payload);
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create
      .addCase(createProjectAction.fulfilled, (state, action) => {
        state.projectList = [...state.projectList, action.payload];
      })
      
      // Update
      .addCase(updateProjectAction.fulfilled, (state, action) => {
        const project = action.payload;
        state.projectList = state.projectList.map(p => 
          p.id === project.id ? project : p
        );
        if (state.currentProject?.id === project.id) {
          state.currentProject = project;
        }
      })
      
      // Delete
      .addCase(deleteProjectAction.fulfilled, (state, action) => {
        const id = action.payload;
        state.projectList = state.projectList.filter(p => p.id !== id);
        state.inactiveProjectList = state.inactiveProjectList.filter(p => p.id !== id);
        if (state.currentProject?.id === id) {
          state.currentProject = null;
          saveCurrentProjectToStorage(null);
        }
      });
  },
});

export const {
  initProjects,
  setProjectList,
  setCurrentProject,
  addProjectList,
  updateProject,
  addProject,
  addInactiveProjectList,
  setProjectProjectArea,
  setProject,
} = projectSlice.actions;

export default projectSlice.reducer;
